import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const _fmtName = (fullName: string) => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) return fullName;
  const last = parts.pop()!;
  return `${last}, ${parts.join(' ')}`;
};
const _norm = (s: string) =>
  s.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const _tok = (t: string, tgt: string): number => {
  if (!t || !tgt) return 0;
  if (tgt === t) return 3;
  if (tgt.startsWith(t)) return 2;
  if (tgt.includes(t)) return 1;
  return 0;
};
// Suffix tokens (JR, SR, I/II/III, IV, V-VIII) are optional:
// they boost the score when they match but don't kill the result when they don't,
// so "Smith Jr" still finds "John Smith" even if the DB has no suffix stored,
// and "Jr" alone finds all Junior bowlers.
const SUFFIX_RE = /^(jr\.?|sr\.?|i{1,3}|iv|vi{0,3})$/i;
const _tokenBagScore = (queryToks: string[], nameToks: string[]): number => {
  if (queryToks.length === 0) return 0;
  const required = queryToks.filter(t => !SUFFIX_RE.test(t));
  const optional = queryToks.filter(t => SUFFIX_RE.test(t));
  // if only suffixes were typed, treat them as required (e.g. searching just "Jr")
  const mustMatch = required.length > 0 ? required : optional;
  const bonusToks = required.length > 0 ? optional : [];
  let total = 0;
  for (const qt of mustMatch) {
    let best = 0;
    for (const nt of nameToks) best = Math.max(best, _tok(qt, nt));
    if (best === 0) return 0; // required token unmatched → no result
    total += best;
  }
  for (const qt of bonusToks) {
    let best = 0;
    for (const nt of nameToks) best = Math.max(best, _tok(qt, nt));
    total += best; // bonus score, not required
  }
  return total;
};
const _bowlerMatch = (query: string, fullName: string): number => {
  const q = _norm(query);
  if (!q) return 0;
  const nameToks = fullName.trim().split(/\s+/).filter(Boolean).map(_norm);
  // comma format: "Smith Jr, John" or "Smith, John Jr" — flatten all tokens into the bag
  if (q.includes(',')) {
    const segs = q.split(',').map(s => s.trim()).filter(Boolean);
    const allToks = segs.flatMap(s => s.split(/\s+/).filter(Boolean));
    return _tokenBagScore(allToks, nameToks);
  }
  return _tokenBagScore(q.split(/\s+/).filter(Boolean), nameToks);
};

interface SideEvent {
  id: number;
  name: string;
  type: string;
  cost: number;
  entry_count: number;
  bracket_size?: number;
  gender?: string | null;
  min_age?: number | null;
  max_age?: number | null;
}

interface GroupBowler {
  id: number;
  name: string;
  email: string;
  gender?: string | null;
  dob?: string | null;
  lane?: number | null;
  letter?: string | null;
  set_average?: number | null;
  league_average?: number | null;
}

interface Props {
  leagueId: number;
  squadId: number;
  sideEvents: SideEvent[];
  onClose: () => void;
  onRefresh: () => void;
  /** When true, renders as a full-page view instead of a floating modal overlay */
  standalone?: boolean;
}

const QuickEntriesModal: React.FC<Props> = ({ leagueId, squadId, sideEvents, onClose, onRefresh, standalone = false }) => {
  const [bowlers, setBowlers] = useState<GroupBowler[]>([]);
  // sideEventId → Set of bowler_ids already entered (non-bracket)
  const [existingParticipants, setExistingParticipants] = useState<Record<number, Set<number>>>({});
  // Newly checked: sideEventId → Set of bowler_ids
  const [checkedEntries, setCheckedEntries] = useState<Record<number, Set<number>>>({});
  const [sortBy, setSortBy] = useState<'default' | 'lastName' | 'lane'>('default');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());
  const highlightId = useMemo(() => {
    if (!nameSearch.trim()) return null;
    let best = 0, bestId: number | null = null;
    for (const b of bowlers) {
      const s = _bowlerMatch(nameSearch, b.name);
      if (s > best) { best = s; bestId = b.id; }
    }
    return best > 0 ? bestId : null;
  }, [nameSearch, bowlers]);
  useEffect(() => {
    if (highlightId != null)
      rowRefs.current.get(highlightId)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlightId, nameSearch]);

  const nonBracketSEs = sideEvents.filter(se => se.type !== 'bracket');

  const isEligible = (bowler: GroupBowler, se: SideEvent): boolean => {
    if (se.gender === 'F' && bowler.gender !== 'F') return false;
    if (se.min_age) {
      if (!bowler.dob) return false;
      const age = Math.floor((Date.now() - new Date(bowler.dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
      if (age < se.min_age) return false;
    }
    if (se.max_age) {
      if (!bowler.dob) return false;
      const age = Math.floor((Date.now() - new Date(bowler.dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
      if (age > se.max_age) return false;
    }
    return true;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const bowlerRes = await axios.get(
        `${API}/bookie-league-management/leagues/${leagueId}/squads/${squadId}/bowlers-with-lanes`,
        { headers: authHeader() }
      );
      setBowlers(bowlerRes.data);

      const participantData: Record<number, Set<number>> = {};
      await Promise.all(
        nonBracketSEs.map(async se => {
          const r = await axios.get(
            `${API}/bookie-league-management/side-events/${se.id}/participants`,
            { headers: authHeader() }
          );
          participantData[se.id] = new Set(r.data as number[]);
        })
      );
      setExistingParticipants(participantData);
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [leagueId, squadId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleSortChange = (newSort: 'default' | 'lastName' | 'lane') => {
    if (newSort === 'lane') {
      const hasLanes = bowlers.some(b => b.lane != null);
      if (!hasLanes) {
        alert('Please assign lanes on the scores page before sorting by lane numbers.');
        return;
      }
    }
    setSortBy(newSort);
  };

  const sortedBowlers = useMemo(() => {
    const sorted = [...bowlers];
    if (sortBy === 'lastName') {
      sorted.sort((a, b) => {
        const aLast = a.name.trim().split(/\s+/).pop() || '';
        const bLast = b.name.trim().split(/\s+/).pop() || '';
        return aLast.localeCompare(bLast);
      });
    } else if (sortBy === 'lane') {
      sorted.sort((a, b) => {
        if (a.lane == null && b.lane == null) return 0;
        if (a.lane == null) return 1;
        if (b.lane == null) return -1;
        return a.lane - b.lane;
      });
    }
    return sorted;
  }, [bowlers, sortBy]);

  const toggleCheck = (seId: number, bowlerId: number) => {
    setCheckedEntries(prev => {
      const next = { ...prev };
      const current = new Set(next[seId] ?? []);
      if (current.has(bowlerId)) current.delete(bowlerId);
      else current.add(bowlerId);
      next[seId] = current;
      return next;
    });
  };

  const handleSaveEntries = async () => {
    setSaving(true);
    setError('');
    try {
      for (const se of nonBracketSEs) {
        const newBowlers = checkedEntries[se.id] ?? new Set<number>();
        for (const bowlerId of Array.from(newBowlers)) {
          if (existingParticipants[se.id]?.has(bowlerId)) continue;
          await axios.post(
            `${API}/bookie-league-management/side-events/${se.id}/entries`,
            { bowlerId, paid: true },
            { headers: authHeader() }
          );
        }
      }
      onRefresh();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to save entries');
    } finally {
      setSaving(false);
    }
  };

  const hasNewEntries = nonBracketSEs.some(se =>
    Array.from(checkedEntries[se.id] ?? []).some(bid => !existingParticipants[se.id]?.has(bid))
  );

  if (standalone) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        <div className="bg-gray-900 flex flex-col flex-1 overflow-hidden" style={{ height: '100vh' }}>
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-4 border-b border-gray-700 flex-shrink-0">
            <div className="flex-1 mr-4">
              <h2 className="text-lg font-semibold text-white">Quick Entries</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Entries must be removed on the Event's tab by clicking the trash can icon. Use this grid to add entries.
              </p>
              <div className="mt-2 flex gap-2">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={nameSearch}
                    onChange={e => setNameSearch(e.target.value)}
                    placeholder="Search bowler — first, last, or both…"
                    autoFocus
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg pl-8 pr-7 py-1.5 text-sm text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none"
                  />
                  <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {nameSearch && (
                    <button onClick={() => setNameSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                <select
                  value={sortBy}
                  onChange={e => handleSortChange(e.target.value as 'default' | 'lastName' | 'lane')}
                  className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1.5 text-sm text-white focus:border-amber-500 focus:outline-none"
                >
                  <option value="default">Sort: Default</option>
                  <option value="lastName">Sort: Last Name</option>
                  <option value="lane">Sort: Lane #</option>
                </select>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors ml-4 mt-0.5" title="Close window">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Body */}
          <div className="flex-1 overflow-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Loading…</div>
            ) : (
              <>
                {error && <div className="mb-4 px-3 py-2 bg-red-900/50 border border-red-700 text-red-300 rounded-lg text-sm">{error}</div>}
                {bowlers.length === 0 ? (
                  <div className="flex items-center justify-center h-32 text-gray-500 text-sm">No bowlers in the group yet.</div>
                ) : (
                  <table className="w-full text-sm border-separate border-spacing-0">
                    <thead className="sticky top-0 z-10 bg-gray-900">
                      <tr>
                        <th className="sticky left-0 bg-gray-900 text-left text-gray-400 font-medium py-2 px-3 border-b border-gray-700 min-w-[160px] z-20">Bowler</th>
                        {nonBracketSEs.map(se => (
                          <th key={se.id} className="bg-gray-900 text-center text-gray-200 font-medium py-2 px-3 border-b border-gray-700 min-w-[100px] whitespace-nowrap">
                            <div>{se.name}</div>
                            <div className="text-xs text-gray-500 font-normal">${se.cost}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedBowlers.map((bowler, i) => (
                        <tr
                          key={bowler.id}
                          ref={(el) => { if (el) rowRefs.current.set(bowler.id, el); else rowRefs.current.delete(bowler.id); }}
                          className={bowler.id === highlightId ? 'bg-amber-900/30' : i % 2 === 0 ? 'bg-gray-800/20' : ''}
                        >
                          <td className={`sticky left-0 py-2 px-3 border-b border-gray-800 font-medium z-10 whitespace-nowrap ${bowler.id === highlightId ? 'bg-amber-900/60 text-amber-100' : 'bg-gray-900 text-gray-200'}`}>
                            <div>{_fmtName(bowler.name)}</div>
                            <div className="flex gap-1.5 flex-wrap mt-0.5">
                              {bowler.lane != null && (
                                <span className="text-[9px] text-gray-400 font-medium">lane {bowler.lane}{bowler.letter ?? ''}</span>
                              )}
                              {bowler.league_average != null && bowler.league_average > 0 && (
                                <span className="text-[9px] text-blue-400 font-medium">calc {Number(bowler.league_average).toFixed(1)}</span>
                              )}
                              {bowler.set_average != null && (
                                <span className="text-[9px] text-amber-400 font-medium">set {Number(bowler.set_average).toFixed(1)}</span>
                              )}
                              {bowler.lane == null && bowler.league_average == null && bowler.set_average == null && (
                                <span className="text-[9px] text-gray-600">#{bowler.id}</span>
                              )}
                            </div>
                          </td>
                          {nonBracketSEs.map(se => {
                            if (!isEligible(bowler, se)) {
                              return <td key={se.id} className="text-center py-2 px-3 border-b border-gray-800"><span className="text-gray-600 text-xs">—</span></td>;
                            }
                            const alreadyIn = existingParticipants[se.id]?.has(bowler.id) ?? false;
                            const isChecked = alreadyIn || (checkedEntries[se.id]?.has(bowler.id) ?? false);
                            return (
                              <td key={se.id} className="text-center py-2 px-3 border-b border-gray-800">
                                <input type="checkbox" checked={isChecked} disabled={alreadyIn}
                                  onChange={() => toggleCheck(se.id, bowler.id)}
                                  className="w-4 h-4 accent-purple-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60" />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-700 flex-shrink-0 gap-4">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">Close Window</button>
            <div className="flex gap-3">
              {nonBracketSEs.length > 0 && (
                <button onClick={handleSaveEntries} disabled={saving || !hasNewEntries}
                  className="px-4 py-2 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors">
                  {saving ? 'Saving…' : 'Save & Collect'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-700 flex-shrink-0">
          <div className="flex-1 mr-4">
            <h2 className="text-lg font-semibold text-white">Quick Entries</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Entries must be removed on the Event's tab by clicking the trash can icon. Use this grid to add entries.
            </p>
            <div className="mt-2 flex gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={nameSearch}
                  onChange={e => setNameSearch(e.target.value)}
                  placeholder="Search bowler — first, last, or both…"
                  autoFocus
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg pl-8 pr-7 py-1.5 text-sm text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none"
                />
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {nameSearch && (
                  <button onClick={() => setNameSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <select
                value={sortBy}
                onChange={e => handleSortChange(e.target.value as 'default' | 'lastName' | 'lane')}
                className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1.5 text-sm text-white focus:border-amber-500 focus:outline-none"
              >
                <option value="default">Sort: Default</option>
                <option value="lastName">Sort: Last Name</option>
                <option value="lane">Sort: Lane #</option>
              </select>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors ml-4 mt-0.5">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto overflow-x-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Loading…</div>
          ) : (
            <>
              {error && (
                <div className="mb-4 px-3 py-2 bg-red-900/50 border border-red-700 text-red-300 rounded-lg text-sm">
                  {error}
                </div>
              )}
              {bowlers.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-gray-500 text-sm">
                  No bowlers in the group yet.
                </div>
              ) : (
                <div>
                  <table className="w-full text-sm border-separate border-spacing-0">
                    <thead className="sticky top-0 z-10 bg-gray-900">
                      <tr>
                        <th className="sticky left-0 bg-gray-900 text-left text-gray-400 font-medium py-2 px-3 border-b border-gray-700 min-w-[160px] z-20">
                          Bowler
                        </th>
                        {nonBracketSEs.map(se => (
                          <th
                            key={se.id}
                            className="bg-gray-900 text-center text-gray-200 font-medium py-2 px-3 border-b border-gray-700 min-w-[100px] whitespace-nowrap"
                          >
                            <div>{se.name}</div>
                            <div className="text-xs text-gray-500 font-normal">${se.cost}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedBowlers.map((bowler, i) => (
                        <tr
                          key={bowler.id}
                          ref={(el) => { if (el) rowRefs.current.set(bowler.id, el); else rowRefs.current.delete(bowler.id); }}
                          className={bowler.id === highlightId ? 'bg-amber-900/30' : i % 2 === 0 ? 'bg-gray-800/20' : ''}
                        >
                          <td className={`sticky left-0 py-2 px-3 border-b border-gray-800 font-medium z-10 whitespace-nowrap ${bowler.id === highlightId ? 'bg-amber-900/60 text-amber-100' : 'bg-gray-900 text-gray-200'}`}>
                            <div>{_fmtName(bowler.name)}</div>
                            <div className="flex gap-1.5 flex-wrap mt-0.5">
                              {bowler.lane != null && (
                                <span className="text-[9px] text-gray-400 font-medium">lane {bowler.lane}{bowler.letter ?? ''}</span>
                              )}
                              {bowler.league_average != null && bowler.league_average > 0 && (
                                <span className="text-[9px] text-blue-400 font-medium">calc {Number(bowler.league_average).toFixed(1)}</span>
                              )}
                              {bowler.set_average != null && (
                                <span className="text-[9px] text-amber-400 font-medium">set {Number(bowler.set_average).toFixed(1)}</span>
                              )}
                              {bowler.lane == null && bowler.league_average == null && bowler.set_average == null && (
                                <span className="text-[9px] text-gray-600">#{bowler.id}</span>
                              )}
                            </div>
                          </td>
                          {nonBracketSEs.map(se => {
                            if (!isEligible(bowler, se)) {
                              return (
                                <td key={se.id} className="text-center py-2 px-3 border-b border-gray-800">
                                  <span className="text-gray-600 text-xs">—</span>
                                </td>
                              );
                            }
                            const alreadyIn = existingParticipants[se.id]?.has(bowler.id) ?? false;
                            const isChecked = alreadyIn || (checkedEntries[se.id]?.has(bowler.id) ?? false);
                            return (
                              <td key={se.id} className="text-center py-2 px-3 border-b border-gray-800">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  disabled={alreadyIn}
                                  onChange={() => toggleCheck(se.id, bowler.id)}
                                  className="w-4 h-4 accent-purple-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-700 flex-shrink-0 gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <div className="flex gap-3">
            {nonBracketSEs.length > 0 && (
              <button
                onClick={handleSaveEntries}
                disabled={saving || !hasNewEntries}
                className="px-4 py-2 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
              >
                {saving ? 'Saving…' : 'Save & Collect'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickEntriesModal;
